/**
 * BottomSheet — V1.9.8
 *
 * Native mobile bottom-sheet modal. On mobile (< md) slides up from the
 * bottom with a swipe-down-to-close drag handle. On desktop (≥ md) falls
 * back to a centered card modal so the same component works everywhere.
 *
 * Usage:
 *   <BottomSheet open={o} onClose={close} title="Filter">
 *       ...content...
 *   </BottomSheet>
 */

import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "@phosphor-icons/react";

export default function BottomSheet({
    open,
    onClose,
    title,
    children,
    footer,
    testId,
    maxHeight = "85vh",
}) {
    // Lock body scroll while open.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    // Close on ESC.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose?.();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="bs-overlay"
                    className="fixed inset-0 z-[60] flex items-end md:items-center md:justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    data-testid={testId ? `${testId}-overlay` : undefined}
                >
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-ink/50"
                        onClick={onClose}
                        aria-hidden="true"
                    />
                    {/* Sheet body */}
                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        drag="y"
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={{ top: 0, bottom: 0.4 }}
                        onDragEnd={(_, info) => {
                            if (info.offset.y > 120 || info.velocity.y > 500) {
                                onClose?.();
                            }
                        }}
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ type: "spring", damping: 32, stiffness: 320 }}
                        className="relative w-full md:w-auto md:max-w-lg md:mx-auto bg-white rounded-t-2xl md:rounded-2xl shadow-2xl overflow-hidden"
                        style={{ maxHeight, touchAction: "none" }}
                        data-testid={testId}
                    >
                        {/* Drag handle (mobile only, decorative) */}
                        <div className="md:hidden flex justify-center pt-2 pb-1">
                            <span className="block w-10 h-1 rounded-full bg-ink/20" />
                        </div>

                        {(title || onClose) && (
                            <div className="flex items-center justify-between px-5 py-3 border-b border-ink/10">
                                <h3 className="font-heading text-base font-black">{title}</h3>
                                {onClose && (
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="p-1.5 rounded-full text-ink/50 hover:text-ink hover:bg-ink/5"
                                        aria-label="Close"
                                        data-testid={testId ? `${testId}-close-btn` : undefined}
                                    >
                                        <X size={16} weight="bold" />
                                    </button>
                                )}
                            </div>
                        )}

                        <div
                            className="overflow-y-auto"
                            style={{ maxHeight: `calc(${maxHeight} - 60px)` }}
                            onPointerDown={(e) => e.stopPropagation()}
                        >
                            {children}
                        </div>

                        {footer && (
                            <div className="border-t border-ink/10 bg-cream/50 px-5 py-3 flex items-center justify-end gap-2 safe-bottom">
                                {footer}
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
